import { useCallback } from "react";

import type { ChatReplyDto, ChatReplyRequest } from "@/api/types";
import type { ChatReplyPayload } from "@/components/ChatWindow/ChatWindow";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";

// Structural interface — intentionally loose to accept both chatMutations and portfolioChatMutations
interface AddChatReplyMutation {
  mutateAsync: (vars: { chatId: string; data: ChatReplyRequest }) => Promise<ChatReplyDto>;
}

interface UseChatReplyHandlerOptions {
  addChatReplyMutation: AddChatReplyMutation;
}

export function useChatReplyHandler({ addChatReplyMutation }: UseChatReplyHandlerOptions): {
  sendReply: (payload: ChatReplyPayload & { chatId: string }) => Promise<ChatReplyDto>;
} {
  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo } = useUploadVideo({});

  const sendReply = useCallback(
    async ({ message, images, videos, chatId }: ChatReplyPayload & { chatId: string }) => {
      const data: ChatReplyRequest = { text: message.trim() };

      if (images.length > 0) {
        const uploadedImage = await uploadFormImage(images[0]);
        if (uploadedImage) {
          data.imageId = uploadedImage.id;
        }
      }

      if (videos.length > 0) {
        const uploadedVideoIds: string[] = [];
        for (const video of videos) {
          const uploadedVideo = await uploadFormVideo(video);
          if (uploadedVideo) {
            uploadedVideoIds.push(uploadedVideo.id);
          }
        }
        data.videoIds = uploadedVideoIds;
      }

      return await addChatReplyMutation.mutateAsync({ chatId, data });
    },
    [addChatReplyMutation, uploadFormImage, uploadFormVideo],
  );

  return { sendReply };
}
